import { useAuth0 } from '@auth0/auth0-react'
import { Box, Container, Image, Text, useDisclosure } from '@chakra-ui/react'
import {
  Disclaimer_Swnz_GlobalSettingsCollectionFragment,
  InventoryProvider,
} from 'content-service'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Fragment, useRef, useState } from 'react'
import {
  FinderResultItemEnquiryButton,
  FinderResultItemSaveButton,
} from 'src/components/Search/FinderResultItemButtons'
import { createUtagStartEnquiryEvent, INVENTORY_LABELS } from 'tracking'
import {
  CtaButton,
  FORM_COMPONENTS,
  RichTextRenderer,
  StudyOptionItemType,
  useHandleModalCloseResetData,
} from 'ui'
import { useEnquiryModalOnSignIn, useSavedModalOnSignIn } from 'ui/src/forms/helpers/enquiryForm'
import { searchStateToURL } from 'utils/src/helpers/algoliaSearch'
import { defaultLocale } from '../../../i18n'
import {
  DetailPageHeader,
  DetailPageHeaderContainer,
  DetailPageHeaderDetail,
} from '../../components/DetailPageHeader'
import SavedSuccessModal, { SavedModalStatusType } from '../../components/SavedSuccessModal'
import SignUpModal, { LoginConnectProps, SignUpType } from '../../components/SignUpModal'
import { EnquiryFormConnectProps } from '../../connectors'
import { ROUTES } from '../../constants'
import useUpdateInventoryItem from '../../hooks/useUpdateInventoryItem'
import { determineRegion, findDefaultCampus, validateProviderDetailProps } from './helpers'
import ProviderDetailCampusesTab from './ProviderDetailCampusesTab'

interface ProviderDetailPageProps {
  providerDetail: InventoryProvider
  disclaimer: Disclaimer_Swnz_GlobalSettingsCollectionFragment | undefined | null
  pageUrl: string
}

const FormModal = dynamic(() => import('ui/src/forms/FormModal'))
const EnquiryFormConnect = dynamic(() => import('../../connectors/EnquiryForm'))

const ProviderDetailPage = ({ providerDetail, disclaimer, pageUrl }: ProviderDetailPageProps) => {
  const isEnquired = useRef<boolean>(false)
  const { locale } = useRouter()
  const { defaultCampus: defaultCampusId } = providerDetail
  const campuses = providerDetail?.campuses?.filter((campus) => campus?.active)
  const validProviderDetailProps = validateProviderDetailProps({
    ...providerDetail,
    campuses,
  })
  const region = determineRegion(campuses)
  const defaultCampus = findDefaultCampus(defaultCampusId, campuses)
  const disclaimerRichText = disclaimer?.items[0]?.richText || null

  /**
   * 1. Hooks and Component state relevant to <CourseEnquiryForm/InstitutionEnquiryForm/> modal.
   */
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure()

  const {
    isOpen: isSignUpOpen,
    onOpen: onSignUpModalOpen,
    onClose: onSignUpModalClose,
  } = useDisclosure()

  const {
    isOpen: isSaveModalOpen,
    onClose: onSaveModalClose,
    onOpen: onSaveModalOpen,
  } = useDisclosure()

  const { isAuthenticated, isLoading } = useAuth0()

  const handleModalCloseResetData = useHandleModalCloseResetData()

  const [currentLoginState, setCurrentLoginState] = useState<LoginConnectProps>({
    openModal: false,
  })

  const [actionData, setActionData] = useState<EnquiryFormConnectProps | null>(null)
  const [savedStatus, setSavedStatus] = useState(SavedModalStatusType.Default)
  const isNZSchool = providerDetail.type?.tag === 'nz-school'
  const subtypeListMap = providerDetail.subtypeList?.map((subtype) => `${subtype?.name}`).join(', ')

  useEnquiryModalOnSignIn(onModalOpen, setActionData)
  useSavedModalOnSignIn(onSignUpModalOpen, setSavedStatus)

  const handleEnquiryFormModalOpen = () => {
    createUtagStartEnquiryEvent({
      pageName: INVENTORY_LABELS.PROVIDER.PAGE_NAME,
      inventoryId: validProviderDetailProps.id,
      inventoryObjectType: INVENTORY_LABELS.PROVIDER.INVENTORY_OBJECT_TYPE,
      levelOfStudy: '',
      areaOfStudyArray: [],
      subjectOfStudyArray: [],
      deliveryModeArray: [],
      courseName: '',
      providerName: validProviderDetailProps.name,
      providerType: validProviderDetailProps.type,
      providerSubtype: validProviderDetailProps.subtypeList,
      campusRegionArray: validProviderDetailProps.campusRegions,
      linkLabel: 'Enquire',
    })
    if (!isLoading) {
      setActionData({
        formId: providerDetail.id as string,
        formComponent: isNZSchool
          ? FORM_COMPONENTS.SCHOOL_ENQUIRY
          : FORM_COMPONENTS.PROVIDER_ENQUIRY,
        providerName: providerDetail.name as string,
        onClose: () => false,
      })

      if (isAuthenticated) {
        onModalOpen()
      } else {
        onSignUpModalOpen()
        setCurrentLoginState({
          loginType: SignUpType.Enquiry,
          openModal: true,
          inventory: {
            formId: providerDetail.id as string,
            formComponent: isNZSchool
              ? FORM_COMPONENTS.SCHOOL_ENQUIRY
              : FORM_COMPONENTS.PROVIDER_ENQUIRY,
            providerName: providerDetail.name as string,
            onClose: () => false,
          },
        })
      }
    }
  }

  const handleSaveItem = useUpdateInventoryItem(onSaveModalOpen)

  const handleSaveClick = async (
    props: EnquiryFormConnectProps & { itemType: StudyOptionItemType }
  ) => {
    if (!isLoading) {
      setActionData(props)
      if (isAuthenticated) {
        handleSaveItem(props.formId, StudyOptionItemType.Provider)
          .then(() => {
            setSavedStatus(SavedModalStatusType.Success)
          })
          .catch((err) => {
            setSavedStatus(SavedModalStatusType.Error)
          })
      } else {
        onSignUpModalOpen()
        setCurrentLoginState({
          loginType: SignUpType.Save,
          openModal: true,
          inventory: props,
        })
      }
    }
  }

  const galleryItems = validProviderDetailProps.profileImages.map((imageSrc) => ({
    image: imageSrc ? (
      <Image
        src={imageSrc}
        alt=""
        h="full"
        objectFit="cover"
        onError={(event) => {
          event.currentTarget.style.display = 'none'
        }}
      />
    ) : null,
  }))

  const allCoursesURL = searchStateToURL(ROUTES.COURSE.RESULTS, {
    menu: { providerName: validProviderDetailProps.name },
    page: 1,
  })

  return (
    <Fragment>
      <Box dir="ltr">
        <DetailPageHeaderContainer>
          <DetailPageHeaderContainer.Header>
            <DetailPageHeader
              linkHeading={subtypeListMap}
              heading={validProviderDetailProps.name}
              logoUrl={validProviderDetailProps.logoUrl}
            />
          </DetailPageHeaderContainer.Header>
          <DetailPageHeaderContainer.Details>
            {validProviderDetailProps.numberOfStudents && !isNZSchool && (
              <DetailPageHeaderDetail
                detailHeading="Number of students"
                detail={`${validProviderDetailProps.numberOfStudents} approx`}
              />
            )}
            {validProviderDetailProps.numberOfInternationalStudents && !isNZSchool && (
              <DetailPageHeaderDetail
                detailHeading="International students"
                detail={`${validProviderDetailProps.numberOfInternationalStudents} approx`}
              />
            )}
            <DetailPageHeaderDetail detailHeading="Region" detail={region} />
          </DetailPageHeaderContainer.Details>
          <DetailPageHeaderContainer.CtaButtons>
            {!isNZSchool && (
              <CtaButton
                href={
                  locale === defaultLocale || locale === 'en'
                    ? `/en${allCoursesURL}`
                    : allCoursesURL
                }
                aria-label="See all courses"
              >
                See all courses
              </CtaButton>
            )}
            {validProviderDetailProps.type !== 'nz-scholarship-only-provider' && (
              <FinderResultItemEnquiryButton
                colorScheme="deepblue"
                openEnquiryForm={handleEnquiryFormModalOpen}
                inventoryId={validProviderDetailProps.id}
                height="60px"
                isEnquired={isEnquired.current}
              />
            )}
            <FinderResultItemSaveButton
              colorScheme="deepblue"
              inventoryId={validProviderDetailProps.id}
              saveInventoryItem={() =>
                handleSaveClick({
                  providerName: validProviderDetailProps.name,
                  formId: validProviderDetailProps.id,
                  formComponent: '',
                  loginType: SignUpType.Save,
                  onClose: () => false,
                  itemType: StudyOptionItemType.Provider,
                })
              }
              height="60px"
            />
          </DetailPageHeaderContainer.CtaButtons>
        </DetailPageHeaderContainer>
        {/* Note: Tabs to be built out in phase 2 */}
        <Container marginBottom={16}>
          <ProviderDetailCampusesTab
            defaultCampus={defaultCampus}
            providerDetail={providerDetail}
            pageUrl={pageUrl}
            galleryItems={galleryItems}
          />
        </Container>
        {/* Disclaimer */}
        <Container>
          <Box
            marginBottom={14}
            paddingTop={10}
            borderTop="1px solid"
            borderTopColor="deepblue.200"
          >
            <Text fontFamily="enz500">Disclaimer</Text>
            <RichTextRenderer richText={disclaimerRichText} />
          </Box>
        </Container>
      </Box>
      {isModalOpen && actionData && (
        <FormModal isOpen={isModalOpen} onClose={() => handleModalCloseResetData(onModalClose)}>
          <EnquiryFormConnect
            {...actionData}
            onClose={() => handleModalCloseResetData(onModalClose)}
            onSuccess={() => {
              isEnquired.current = true
            }}
          />
        </FormModal>
      )}
      {!isAuthenticated && (
        <SignUpModal
          loginState={currentLoginState}
          isOpen={isSignUpOpen}
          onClose={onSignUpModalClose}
        />
      )}
      <SavedSuccessModal
        loginState={currentLoginState}
        isOpen={isSaveModalOpen}
        onClose={() => handleModalCloseResetData(onSaveModalClose)}
        itemType="providers"
        status={savedStatus}
      />
    </Fragment>
  )
}

export default ProviderDetailPage
